import { inject, injectable } from 'inversify';
import { Observable } from 'knockout';
import { wccModules } from '../../enums/wccModules';
import { CookieClassification, CookieHelpers } from '../../helpers/cookie';
import './cookieconsent.html';
import './cookieconsent.scss';

interface CookieConsentVMConfig {
    context: any;
    openPolicyInNewWindow: boolean | undefined;
}

interface ICookiePolicyOverrideParam extends URLSearchParams {
    CookiePolicy: string | undefined;
}

//
//  This component controls the users choices for cookies to accept.
//
@injectable()
export default class CookieConsentComponent {

    labels = labels;

    showCookieSettings = ko.observable(false);
    hasConsent = ko.observable(false);  // this is used to update observers when the user changes their cookie consent

    consentChanged?: Observable<boolean | undefined>;

    toggleCookieClassificationFunctionality = ko.observable(true);
    toggleCookieClassificationAnalytics = ko.observable(true);

    cookieClassification = ko.pureComputed<CookieClassification>(() => this.getCookieClassificationChoices(CookieClassification.Essential));
    acceptText = ko.pureComputed<string>(() => this.toggleCookieClassificationFunctionality() && this.toggleCookieClassificationAnalytics() ? labels.CookieConsent_ButtonAcceptAll : labels.CookieConsent_ButtonAccept);

    policyLink = ko.pureComputed<string>(() => settings.isAdmin ? '/home/cookiepolicy' : '/cookiepolicy');
    openPolicyInNewWindow = ko.observable(false);

    labelCookieParagraph2 = ko.pureComputed(() => {
        let link = '<a href="' + this.policyLink() + '" target="' + (this.openPolicyInNewWindow() ? '_blank' : '') + '">' + labels.CookieConsent_CookiePolicy +'</a>';
        return labels.CookieConsent_Paragraph2.replace('{0}', link);
    });

    constructor(
        @inject(wccModules.componentConfig) config: CookieConsentVMConfig,
        @inject(wccModules.componentInfo) info: ko.components.ComponentInfo
    ) {
        //set out global so we can call this from anywhere in our app
        window.qualzyCookieConsent = this;

        // had to set this up in order to wire it up properly with old style WCC page implemenations
        // this is used in conjunction with the ref param for components
        if (config.context && config.context.cookieConsentInstance) {
            config.context.cookieConsentInstance = this;            
        }

        if (config.openPolicyInNewWindow) {
            this.openPolicyInNewWindow(config.openPolicyInNewWindow);
        }

        const params = new Proxy(new URLSearchParams(window.location.search), {
            get: (searchParams, prop: string) => searchParams.get(prop),
        });

        //we want a URL by-pass
        const cookiePolicyAutoAgree = (params as ICookiePolicyOverrideParam).CookiePolicy == 'AutoAgree';

        if (cookiePolicyAutoAgree) {
            // save a cookie 'cookie-consent' with essential only
            CookieHelpers.setCookie(CookieHelpers.QUALZY_COOKIE_CONSENT, CookieClassification.Essential, 365, CookieClassification.Essential);

            this.hasConsent(true);
        }
        else {
            this.onShowCookieConsent();
        }
    }

    getConsent() {
        let cookieConsent = CookieHelpers.getCookie(CookieHelpers.QUALZY_COOKIE_CONSENT);

        if (cookieConsent != null && !isNaN(parseInt(cookieConsent))) {
            this.hasConsent(true);
            return parseInt(cookieConsent);
        }
        return 0;
    }

    onShowCookieConsent() {
        if (this.getConsent() <= 0) {
            $('#qualzy-cookie-confirmation').modal('show');
        }
    }

    onEditCookieConsent() {
        let classification = this.getConsent();

        if (classification > 0) {
            this.toggleCookieClassificationFunctionality((classification & CookieClassification.PerformanceAndFunctionality) != 0);
            this.toggleCookieClassificationAnalytics((classification & CookieClassification.AnalyticsAndCustomisation) != 0);
            this.showCookieSettings(true);
        }
        
        $('#qualzy-cookie-confirmation').modal('show');
    }

    onShowCookieSettings() {
        if (!this.hasConsent()) {   // if we don't have consent then we need to reset the toggles
            this.toggleCookieClassificationFunctionality(false);
            this.toggleCookieClassificationAnalytics(false);
        }
        this.showCookieSettings(!this.showCookieSettings());
    }

    getCookieClassificationChoices(classification: CookieClassification) {
        return classification |
            (this.toggleCookieClassificationFunctionality() ? CookieClassification.PerformanceAndFunctionality : 0) |
            (this.toggleCookieClassificationAnalytics() ? CookieClassification.AnalyticsAndCustomisation : 0);
    }

    onAccept() {
        // save a cookie 'cookie-consent'
        CookieHelpers.setCookie(CookieHelpers.QUALZY_COOKIE_CONSENT, this.cookieClassification(), 365, CookieClassification.Essential);

        this.hasConsent(true);

        $('#qualzy-cookie-confirmation').modal('hide');
    }

    onDecline() {
        // save a cookie 'cookie-consent' with essential only
        CookieHelpers.setCookie(CookieHelpers.QUALZY_COOKIE_CONSENT, CookieClassification.Essential, 365, CookieClassification.Essential);

        this.hasConsent(true);

        $('#qualzy-cookie-confirmation').modal('hide');
    }

    onClose() {
        if (this.getConsent() <= 0) {
            this.onDecline();
        }
        else {
            // if no changes were made just close
            $('#qualzy-cookie-confirmation').modal('hide');
        }
    }
}